/**
 * /overview on the enterprise domain.
 *
 * WHY IT EXISTS. www.pulseofp3.org/partner stays live and untouched. This is
 * the version a visitor reads when we send them the enterprise link directly:
 * the institutional story, the pricing and the security page, all on one
 * host, with no bounce back to the marketing domain in the middle of a
 * buying decision. It answered at /partner until Sep 9 2026; that path now
 * redirects here.
 *
 * WHAT IT IS NOT. It is not a port of the marketing copy. The marketing page
 * and this domain's own /trust page contradict each other on five points,
 * and hosting both here would put the contradiction two clicks apart. Every
 * rewrite is recorded in `~/app/lib/partner.ts` against the sentence it
 * replaces.
 *
 * WHAT IS PORTED IS THE LAYOUT (Thomas, Sep 9 2026). Why now is the
 * marketing partner page's icon grid on the maroon band (three cards since
 * Sep 19 2026); Who it is for is its photo-card format; How it works is the
 * for-students page's "four simple steps". The styles are section 16 of
 * gateway.css, each value cited from the file it came from. The "four layers
 * of data" 2x2 moved to /security on Sep 19 2026 (Thomas), between the
 * documents and the gaps.
 */

import type { ReactNode } from "react";

import { GatewayHero, HeroAccent } from "~/app/components/GatewayHero";
import { GatewayShell } from "~/app/components/GatewayShell";
import { LogoCarousel } from "~/app/components/LogoCarousel";
import { ShowcaseArt } from "~/app/components/ShowcaseArt";
import { buttonClass } from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import { formatMoney } from "~/app/lib/format";
import {
  DEMO_BAND,
  AUDIENCES,
  HERO,
  OVERVIEW_STATS,
  SHOWCASE,
  WHY_NOW,
  type ReasonIcon,
} from "~/app/lib/partner";
import { ENTRY_BAND, LEGAL } from "~/app/lib/pricing";

export interface PartnerProps {
  /** Off when a host renders its own chrome. */
  chrome?: boolean;
}

/* ==========================================================================
   Section head: the marketing site's pill, big heading and sub, centred.
   ========================================================================== */

function OverviewHead({
  id,
  tag,
  title,
  sub,
}: {
  id: string;
  tag: string;
  title: ReactNode;
  sub?: string;
}) {
  return (
    <div className="eg-ov-head">
      <span className="eg-ov-tag">{tag}</span>
      <h2 id={id}>{title}</h2>
      {sub ? <p className="eg-ov-sub">{sub}</p> : null}
    </div>
  );
}

/* ==========================================================================
   Icons. Inline, 24-box, 2px stroke, currentColor, the marketing site's
   glyph weight. Self-hosted for the same reason the rail's are.
   ========================================================================== */

function Icon({ d, className }: { d: ReactNode; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {d}
    </svg>
  );
}

const REASON_ICON: Record<ReasonIcon, ReactNode> = {
  ai: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2" />
    </>
  ),
  reporting: (
    <>
      <path d="M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7" />
      <path d="M12 2 20 7H4Z" />
    </>
  ),
  network: (
    <>
      <circle cx="12" cy="5" r="2.5" />
      <circle cx="5" cy="19" r="2.5" />
      <circle cx="19" cy="19" r="2.5" />
      <path d="M12 7.5v4M12 11.5l-5.5 5M12 11.5l5.5 5" />
    </>
  ),
};


/* ==========================================================================
   The page
   ========================================================================== */

export function Partner({ chrome = true }: PartnerProps) {
  return (
    <GatewayShell
      current="partner"
      chrome={chrome}
      mainId="partner-main"
      skipLabel="Skip to the overview"
    >
      <section className="eg-hero">
        <GatewayHero
          title={
            <>
              {HERO.lead} <HeroAccent>{HERO.accent}</HeroAccent>.
            </>
          }
          sub={HERO.sub}
          stats={OVERVIEW_STATS}
        />
      </section>

      <LogoCarousel />

      {/* ================================================================
          Why now. The marketing partner page's band: three icon cards in
          one row on the maroon gradient (five until Sep 19 2026, Thomas).
          External market context, so nothing here depends on P3 holding data
          it does not have.
          ================================================================ */}
      <section className="eg-ov-band" aria-labelledby="why-heading">
        <span className="eg-ov-band-bg" aria-hidden="true" />
        <OverviewHead
          id="why-heading"
          tag="Why now"
          title={
            <>
              The gap your students face is a{" "}
              <span className="eg-accent">network gap</span>
            </>
          }
          sub="Not effort, and not ambition. The people who open doors are the people they have not met."
        />
        <ul className="eg-why-grid">
          {WHY_NOW.map((reason) => (
            <li className="eg-why-card" key={reason.title}>
              <span className="eg-why-ico" aria-hidden="true">
                <Icon d={REASON_ICON[reason.icon]} />
              </span>
              <h3>{reason.title}</h3>
              <p>{reason.detail}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* ================================================================
          Who it is for. The marketing page's photo cards: a 180px band, a
          centred title and role line, four checks, and one action.
          ================================================================ */}
      <section className="eg-ov-sec" aria-labelledby="who-heading">
        <OverviewHead
          id="who-heading"
          tag="Who it is for"
          title={
            <>
              Built for those who believe in{" "}
              <span className="eg-accent">student success</span>
            </>
          }
          sub="P3 meets your students where they are: on their phones."
        />
        <div className="eg-ws-grid">
          {AUDIENCES.map((audience) => (
            <article className="eg-ws-card" key={audience.kind}>
              <div className="eg-ws-vis">
                <img src={audience.image} alt={audience.alt} loading="lazy" decoding="async" />
              </div>
              <div className="eg-ws-body">
                <h3>{audience.kind}</h3>
                <p className="eg-ws-role">{audience.roles}</p>
                {audience.lede ? <p>{audience.lede}</p> : null}
                <ul className="eg-ticks eg-ws-list">
                  {audience.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* How it works moved to /pricing, under "What is included" (Thomas,
          Sep 21 2026). The steps answer "what happens after we buy", which a
          reader asks with the price in front of them, and /overview was
          running two "Built for..." headings back to back. The band itself is
          components/HowItWorks.tsx. */}

      {/* ================================================================
          Inside the dashboard (Thomas, Sep 19 2026). One real screen, the
          matching board captured from the sample, on the dark card; then six
          drawings of live features in the step tints above, a title and one
          line each. The copy and the checks behind it are SHOWCASE in
          partner.ts; the drawings are ShowcaseArt.tsx.
          ================================================================ */}
      <section className="eg-ov-sec" aria-labelledby="inside-heading">
        <OverviewHead
          id="inside-heading"
          tag={SHOWCASE.tag}
          title={
            <>
              {SHOWCASE.lead} <span className="eg-accent">{SHOWCASE.accent}</span>
            </>
          }
          sub={SHOWCASE.sub}
        />
        <div className="eg-sc">
          <article className="eg-sc-hero">
            <div className="eg-sc-copy">
              <span className="eg-sc-tag">{SHOWCASE.hero.tag}</span>
              <h3>
                {SHOWCASE.hero.lead} <span className="eg-sc-accent">{SHOWCASE.hero.accent}</span>
              </h3>
              <p>{SHOWCASE.hero.detail}</p>
              <ul className="eg-sc-legend">
                {SHOWCASE.hero.legend.map((item) => (
                  <li key={item.label}>
                    <svg viewBox="0 0 44 12" aria-hidden="true" focusable="false">
                      <path
                        className={cn("is-" + item.line, item.line === "dashed" && "eg-sc-flow p12")}
                        d="M2 9C14 9 20 3 42 3"
                      />
                    </svg>
                    {item.label}
                  </li>
                ))}
              </ul>
              <a className="eg-sc-link" href={SHOWCASE.hero.href}>
                {SHOWCASE.hero.cta}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </a>
            </div>
            <figure className="eg-sc-frame">
              <div className="eg-sc-bar" aria-hidden="true">
                <span className="eg-sc-dots">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="eg-sc-address">{SHOWCASE.hero.address}</span>
              </div>
              <img
                src={SHOWCASE.hero.image}
                width={SHOWCASE.hero.imageWidth}
                height={SHOWCASE.hero.imageHeight}
                alt={SHOWCASE.hero.alt}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </article>
          <div className="eg-sc-grid">
            {SHOWCASE.cards.map((card) => (
              <article className={cn("eg-sc-card", `tone-${card.tone}`, !card.onPhone && "is-wide-only")} key={card.art}>
                <div className="eg-sc-vis">
                  <ShowcaseArt art={card.art} />
                </div>
                <div className="eg-sc-body">
                  <h3>{card.title}</h3>
                  <p>{card.detail}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          Try it now. The platform page's demo band (.wa-demo-band), ported
          with its cited values (gateway.css section 17): centred, the campus
          photo under a cream veil, one CTA. The link is the sample dashboard
          on this host.
          ================================================================ */}
      <section className="eg-demo-band" aria-labelledby="demo-heading">
        <span className="eg-demo-band-bg" aria-hidden="true" />
        <div className="eg-demo-band-in">
          <span className="eg-ov-tag">{DEMO_BAND.tag}</span>
          <h2 id="demo-heading">
            {DEMO_BAND.title}
            <br />
            <span className="eg-accent">{DEMO_BAND.accent}</span>
          </h2>
          <p className="eg-demo-lede">{DEMO_BAND.lede}</p>
          <a className={cn(buttonClass("primary", "lg"), "eg-demo-cta")} href={DEMO_BAND.href}>
            {DEMO_BAND.cta}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
        </div>
      </section>

      {/* ================================================================
          Close. One action, to the page that prices it, on the same dark
          surface the page opened with. The background span is what the
          Sep 9 fix restored: without it the band's white type sat on cream.
          ================================================================ */}
      <section className="eg-close" aria-labelledby="close-heading">
        <span className="eg-close-bg" aria-hidden="true">
          <span className="eg-close-bloom" />
        </span>
        <div className="eg-close-grid">
          <div className="eg-close-in">
            <p className="eg-band-eyebrow">Ready when you are</p>
            <h2 id="close-heading">
              Start at{" "}
              <span className="eg-accent">{formatMoney(ENTRY_BAND.monthly ?? 0)} a month</span>.
            </h2>
            <p className="eg-close-sub">
              Pick a capacity, or ask for a quote your purchasing office can process.
            </p>
            <div className="eg-close-actions">
              <a className={cn(buttonClass("primary", "lg"), "eg-cta-primary")} href="/pricing">
                See pricing
              </a>
              <a className="eg-close-alt" href={`mailto:${LEGAL.contactEmail}`}>
                Talk to us instead
              </a>
            </div>
          </div>
          <div className="eg-close-fine">
            <p>
              <strong>Self-serve, or a purchase order.</strong> Card today, or a
              quote, a W-9 and an invoice on Net 30 for a purchasing office.
            </p>
          </div>
        </div>
      </section>
    </GatewayShell>
  );
}
